import api from './client'
import type { Message, Therapist } from '../stores/chatStore'

// API returns snake_case, but we need camelCase for our store
interface ApiMessage {
  id: number
  role: 'user' | 'assistant' | 'system'
  content: string
  media_type?: string
  mediaType?: string  // In case backend sends camelCase
  emotion_detected?: string
  emotionDetected?: string
  created_at?: string
  createdAt?: string
  voice_url?: string
  voiceUrl?: string
  video_url?: string
  videoUrl?: string
  video_urls?: string[]
  videoUrls?: string[]
  image_url?: string
  imageUrl?: string
  duration?: number
  reply_to_message_id?: number
  replyToMessageId?: number
  quoted_text?: string
  quotedText?: string
  status?: 'sending' | 'sent' | 'seen'
}

interface ChatHistoryResponse {
  messages: ApiMessage[]
  hasMore: boolean
  sessionId: number
}

// Map API snake_case to store camelCase
function mapApiMessage(msg: ApiMessage): Message {
  return {
    id: msg.id,
    role: msg.role,
    content: msg.content,
    mediaType: msg.mediaType || msg.media_type || 'text',
    emotionDetected: msg.emotionDetected || msg.emotion_detected,
    createdAt: msg.createdAt || msg.created_at || new Date().toISOString(),
    voiceUrl: msg.voiceUrl || msg.voice_url,
    videoUrl: msg.videoUrl || msg.video_url,
    videoUrls: msg.videoUrls || msg.video_urls || undefined,
    imageUrl: msg.imageUrl || msg.image_url,
    duration: msg.duration,
    status: msg.status || 'seen',  // Default to 'seen' for historical messages
    replyToMessageId: msg.replyToMessageId || msg.reply_to_message_id,
    quotedText: msg.quotedText || msg.quoted_text,
  }
}

export interface SessionSummary {
  id: number
  therapist: string
  state: string
  started_at: string
  ended_at?: string
  message_count: number
  summary?: string
}

interface SessionListResponse {
  sessions: SessionSummary[]
  total: number
}

export async function getChatHistory(
  sessionId?: number,
  limit = 50,
  beforeId?: number
): Promise<{ messages: Message[]; has_more: boolean; session_id: number }> {
  const params = new URLSearchParams()
  if (sessionId) params.append('session_id', String(sessionId))
  if (limit) params.append('limit', String(limit))
  if (beforeId) params.append('before_id', String(beforeId))

  const response = await api.get<ChatHistoryResponse>(`/chat/history?${params}`)

  // Map API snake_case to store camelCase
  return {
    messages: response.data.messages
      .filter(msg => msg.role !== 'system')  // System messages are debug-only (hidden agents)
      .map(msg => ({ ...mapApiMessage(msg), sessionId: response.data.sessionId })),
    has_more: response.data.hasMore,
    session_id: response.data.sessionId,
  }
}

export async function getSessions(
  limit = 20,
  offset = 0
): Promise<SessionListResponse> {
  const response = await api.get<SessionListResponse>(
    `/chat/sessions?limit=${limit}&offset=${offset}`
  )
  return response.data
}

export async function getPreviousSession(
  sessionId: number,
  therapist?: string
): Promise<SessionSummary | null> {
  try {
    const params = new URLSearchParams({ session_id: String(sessionId) })
    if (therapist) params.append('therapist', therapist)
    const response = await api.get<SessionSummary>(
      `/chat/sessions/before?${params}`
    )
    return response.data
  } catch {
    return null
  }
}

export interface TherapistResponse {
  type: string
  name: string
  avatarUrl: string
  voiceId?: string
  onboardingCompleted: boolean
}

export async function getCurrentTherapist(): Promise<TherapistResponse> {
  const response = await api.get<{
    type: string
    name: string
    avatar_url: string
    voice_id?: string
    onboarding_completed: boolean
  }>('/chat/therapist')

  // Sync onboarding state to localStorage
  if (response.data.onboarding_completed) {
    localStorage.setItem('onboarding_complete', 'true')
  }

  return {
    type: response.data.type,
    name: response.data.name,
    avatarUrl: response.data.avatar_url,
    voiceId: response.data.voice_id,
    onboardingCompleted: response.data.onboarding_completed,
  }
}

export async function setTherapist(therapistType: string): Promise<Therapist> {
  const response = await api.put<{
    type: string
    name: string
    avatar_url: string
    voice_id?: string
  }>(`/chat/therapist?therapist_type=${therapistType}`)

  return {
    type: response.data.type,
    name: response.data.name,
    avatarUrl: response.data.avatar_url,
    voiceId: response.data.voice_id,
  }
}

export async function getAvailableTherapists(): Promise<{
  therapists: Array<{
    type: string
    name: string
    description: string
    avatar_url: string
    is_current: boolean
  }>
}> {
  const response = await api.get<{
    therapists: Array<{
      type: string
      name: string
      description: string
      avatar_url: string
      is_current: boolean
    }>
  }>('/chat/therapists')

  return response.data
}

export async function sendMessage(
  content: string,
  sessionId?: number
): Promise<void> {
  await api.post('/chat/message', {
    content,
    session_id: sessionId,
    media_type: 'text',
  })
}
